import { motion } from "framer-motion";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useRef } from "react";
import { shiftISO, hoyISOBA } from "@/components/ClockBA";

export const inputCls = "rounded-xl border border-white/10 bg-[#0B0F14] px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-[#2DD4BF] [color-scheme:dark]";
export const btnPrim = "rounded-xl bg-[#2DD4BF] px-4 py-2 text-sm font-bold text-black transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40";
export const btnSec = "rounded-xl border border-white/10 bg-[#151A21] px-3.5 py-2 text-sm font-semibold text-gray-300 transition-colors hover:border-[#2DD4BF]/50 hover:text-white disabled:opacity-40";
export const cardCls = "rounded-2xl border border-white/10 bg-[#151A21]";

export const TONOS = {
  verde: "bg-[#2DD4BF]/15 text-[#2DD4BF]",
  amarillo: "bg-amber-400/15 text-amber-300",
  rojo: "bg-red-400/15 text-red-300",
  azul: "bg-sky-400/15 text-sky-300",
  violeta: "bg-violet-400/15 text-violet-300",
  morado: "bg-fuchsia-400/15 text-fuchsia-300",
  gris: "bg-white/10 text-gray-400",
};

export const BADGE_ESTADO = {
  verde: TONOS.verde,
  amarillo: TONOS.amarillo,
  rojo: TONOS.rojo,
  azul: TONOS.azul,
  violeta: TONOS.violeta,
  morado: TONOS.morado,
  gris: TONOS.gris,
};

export const MESES_NOMBRES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

export function MesNav({ mes, onChange, testid }) {
  const [y, m] = mes.split("-").map(Number);
  const mover = (d) => {
    const nd = new Date(y, m - 1 + d, 1);
    onChange(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <button onClick={() => mover(-1)} className="rounded-xl border border-white/10 bg-[#151A21] p-2 text-gray-400 transition-colors hover:border-[#2DD4BF]/50 hover:text-white" data-testid={`${testid}-anterior`}>
        <ChevronLeft size={16} />
      </button>
      <span className="min-w-44 rounded-xl border border-white/10 bg-[#151A21] px-3.5 py-2 text-center text-sm font-semibold capitalize text-white" data-testid={`${testid}-label`}>
        {MESES_NOMBRES[m - 1]} de {y}
      </span>
      <button onClick={() => mover(1)} className="rounded-xl border border-white/10 bg-[#151A21] p-2 text-gray-400 transition-colors hover:border-[#2DD4BF]/50 hover:text-white" data-testid={`${testid}-siguiente`}>
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

export function PageHeader({ titulo, subtitulo, children, testid }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4" data-testid={testid}>
      <div>
        <h1 className="text-[28px] font-bold leading-tight tracking-tight text-white md:text-[30px]">{titulo}</h1>
        {subtitulo && <p className="mt-1.5 max-w-2xl text-[15px] text-gray-400">{subtitulo}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Card({ titulo, subtitulo, right, children, className = "", bodyCls = "", testid }) {
  return (
    <section className={`${cardCls} ${className}`} data-testid={testid}>
      {(titulo || right) && (
        <header className="flex flex-wrap items-start justify-between gap-2 border-b border-white/8 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-white">{titulo}</h2>
            {subtitulo && <p className="mt-0.5 text-[13px] text-gray-500">{subtitulo}</p>}
          </div>
          {right}
        </header>
      )}
      <div className={bodyCls || "p-5"}>{children}</div>
    </section>
  );
}

export function Kpi({ icon: Icon, label, value, sub, tone = "gris", testid }) {
  return (
    <div className={`${cardCls} p-5`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-gray-400">{label}</p>
        {Icon && (
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONOS[tone]}`}>
            <Icon size={17} />
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-nowrap font-mono-num text-[22px] font-bold leading-tight text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

export function Avatar({ iniciales, size = "md", className = "" }) {
  const sz = {
    sm: "h-8 w-8 text-[11px] rounded-full",
    md: "h-10 w-10 text-xs rounded-full",
    lg: "h-12 w-12 text-sm rounded-full",
    xl: "h-16 w-16 text-xl rounded-2xl",
  }[size];
  return (
    <span className={`flex shrink-0 items-center justify-center bg-[#2DD4BF]/15 font-bold text-[#2DD4BF] ${sz} ${className}`}>
      {iniciales}
    </span>
  );
}

export function FechaNav({ fecha, onChange, testid }) {
  const ref = useRef(null);
  const dd = `${fecha.slice(8, 10)}/${fecha.slice(5, 7)}/${fecha.slice(0, 4)}`;
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <button
        onClick={() => onChange(shiftISO(fecha, -1))}
        className="rounded-xl border border-white/10 bg-[#151A21] p-2 text-gray-400 transition-colors hover:border-[#2DD4BF]/50 hover:text-white"
        data-testid={`${testid}-anterior`}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        onClick={() => ref.current?.showPicker?.() ?? ref.current?.click()}
        className="relative flex items-center gap-2 rounded-xl border border-white/10 bg-[#151A21] px-3.5 py-2 font-mono-num text-sm font-semibold text-white transition-colors hover:border-[#2DD4BF]/50"
        data-testid={`${testid}-fecha`}
      >
        {dd} <CalendarDays size={15} className="text-gray-500" />
        <input
          ref={ref}
          type="date"
          value={fecha}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          tabIndex={-1}
          data-testid={`${testid}-input`}
        />
      </button>
      <button
        onClick={() => onChange(shiftISO(fecha, 1))}
        className="rounded-xl border border-white/10 bg-[#151A21] p-2 text-gray-400 transition-colors hover:border-[#2DD4BF]/50 hover:text-white"
        data-testid={`${testid}-siguiente`}
      >
        <ChevronRight size={16} />
      </button>
      <button onClick={() => onChange(hoyISOBA())} className={btnSec} data-testid={`${testid}-hoy`}>
        Hoy
      </button>
    </div>
  );
}

export function Modal({ titulo, onClose, children, ancho = "max-w-lg", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className={`max-h-[90vh] w-full ${ancho} overflow-y-auto rounded-2xl border border-white/10 bg-[#151A21] p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{titulo}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white/5 hover:text-white" data-testid="modal-cerrar">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </motion.div>
  );
}

export function Badge({ color = "gris", children, testid }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${BADGE_ESTADO[color]}`} data-testid={testid}>
      {children}
    </span>
  );
}

export const TIPO_ALERTA = {
  tardanza: { label: "Tardanza", color: "amarillo" },
  pin_bloqueado: { label: "PIN bloqueado", color: "rojo" },
  sin_salida: { label: "Sin salida", color: "rojo" },
  retiro_anticipado: { label: "Retiro anticipado", color: "amarillo" },
  ausencia: { label: "Ausencia", color: "rojo" },
  justificativo_pendiente: { label: "Justificativo pendiente", color: "azul" },
};

export const TIPO_JUST = {
  turno_medico: "Turno médico",
  tardanza: "Tardanza",
  falta_dia_entero: "Falta de día entero",
  retiro_anticipado: "Retiro anticipado",
  otro: "Otro",
};

export const LICENCIAS = [
  { id: "enfermedad", label: "Enfermedad", dias: null, ayuda: "según certificado" },
  { id: "matrimonio", label: "Matrimonio", dias: 10 },
  { id: "nacimiento", label: "Nacimiento de hijo/a", dias: 2 },
  { id: "fallecimiento", label: "Fallecimiento de familiar directo", dias: 3 },
  { id: "mudanza", label: "Mudanza", dias: 2 },
  { id: "examen", label: "Examen", dias: 2, ayuda: "por examen" },
  { id: "maternidad", label: "Maternidad", dias: 90 },
];
